import { Alert, Button, Divider, InputField } from '@design-system';
import { useTranslation } from 'next-i18next';
import React, { FormEvent, ReactNode, useState } from 'react';

import type {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { PreOrderHeader } from '../PurchaseForm/flows/PreOrderHeader';

export type Props<T extends ProductPurchaseDetails> = {
  children: ReactNode;
  purchasingController: ProductPurchasingController<T>;
};

export default function UnlockProductForm<T extends ProductPurchaseDetails>(
  props: Props<T>,
) {
  const { t } = useTranslation('product');
  const [code, setCode] = useState('');
  const [showValidation, setShowValidation] = useState(true);
  const {
    purchasingController: {
      product,
      requiresUnlock,
      unlockLoading,
      unlockMessage,
      unlockProduct,
      unlocked,
    },
  } = props;

  if (!product || !product.requiresUnlockCode || !requiresUnlock) {
    return <>{props.children}</>;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setShowValidation(true);
    unlockProduct(code);
  }

  const showCodeValidation =
    showValidation && unlockMessage === 'UNLOCK_FAILED' && !unlockLoading;

  return (
    <>
      <PreOrderHeader
        active
        className="mb-6"
        title={t('preOrder.isOpen.title')}
      />
      <div className="flex w-full flex-col gap-y-6">
        <Divider variant="dashed" />
        {unlockMessage === 'UNLOCK_EXPIRED' && (
          <Alert data-testid="unlock-expired-message" status="error">
            {t('unlockCode.expired')}
          </Alert>
        )}
        {unlockMessage === 'UNLOCK_SUCCESS' && (
          <Alert data-testid="unlock-success-message" status="success">
            {t('unlockCode.success')}
          </Alert>
        )}
        {(unlockMessage === undefined || unlockMessage === 'UNLOCK_FAILED') && (
          <form
            className="flex w-full flex-col gap-y-6"
            data-testid="unlock-form"
            onSubmit={handleSubmit}
          >
            <InputField
              dataTestid="unlock-code-input"
              error={showCodeValidation ? t('unlockCode.invalid') : undefined}
              id="code"
              label={t('unlockCode.inputLabel')}
              name="code"
              onChange={(e) => {
                setCode(e.currentTarget.value);
                setShowValidation(false);
              }}
              placeholder={t('unlockCode.inputPlaceholder')}
              type="text"
            />
            <Button
              disabled={unlockLoading}
              fullWidth
              label={
                unlockLoading
                  ? t('unlockCode.buttonLoadingLabel')
                  : t('unlockCode.buttonLabel')
              }
              type="submit"
              variant="primary"
            />
          </form>
        )}
        {unlockMessage === 'UNLOCK_EXPIRED' || (unlocked && props.children)}
      </div>
    </>
  );
}
